import type { SVGProps } from 'react';
import type { ObligationRole } from '@vivport/backend/types';
import { cn } from '@/lib/utils';

const BADGE_FILL = 'hsl(var(--destructive))';
const BADGE_BORDER = 'hsl(var(--destructive))';
const FIGURE = 'hsl(var(--destructive-foreground))';

const OUTSTANDING_FILL = 'hsl(var(--warning))';
const OUTSTANDING_BORDER = 'hsl(var(--warning))';

function badge(fill = BADGE_FILL, border = BADGE_BORDER) {
  return (
    <rect x="0.75" y="0.75" width="22.5" height="22.5" rx="5" fill={fill} stroke={border} strokeWidth={1.4} />
  );
}

// Figures are authored in a 100x100 space and scaled/centered into the 24x24 badge.

/**
 * The member's obligation-role badge for a content row's subtree rollup. ACTIONEE → running,
 * ORIGINATOR → sitting, OUTSTANDING → plain amber badge. When there is no role, renders a same-size blank only if
 * `reserveSpace` is set (so list rows can keep their columns aligned).
 */
export function ObligationRoleIcon({
  role,
  reserveSpace = false,
  className,
}: {
  role: ObligationRole | null | undefined;
  reserveSpace?: boolean;
  className?: string;
}) {
  const sizing = cn('h-5 w-5 shrink-0', className);
  if (role === 'ACTIONEE') {
    return <RunningStickFigure className={sizing} aria-label="Awaiting your action" />;
  }
  if (role === 'ORIGINATOR') {
    return <SittingStickFigure className={sizing} aria-label="Waiting on others" />;
  }
  if (role === 'OUTSTANDING') {
    return <OutstandingBadge className={sizing} aria-label="Outstanding obligations" />;
  }
  return reserveSpace ? <span className={sizing} aria-hidden="true" /> : null;
}

/** ACTIONEE — red badge with a white running figure (the member is being waited on to act). */
export function RunningStickFigure(props: SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" {...props}>
      {badge()}
      <g transform="translate(2.38 2.33) scale(0.185)">
        <g fill="none" stroke={FIGURE} strokeWidth={9} strokeLinecap="round" strokeLinejoin="round">
          <line x1="57" y1="33" x2="37" y2="61" />
          <polyline points="57,36 76,46 88,35" />
          <polyline points="57,36 29,40 23,54" />
          <polyline points="37,61 59,67 66,87" />
          <polyline points="37,61 26,78 16,92" />
        </g>
        <circle cx="66" cy="19" r="11" fill={FIGURE} />
      </g>
    </svg>
  );
}

/** ORIGINATOR — red badge with a white figure seated on a chair (the member is waiting for someone else). */
export function SittingStickFigure(props: SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" {...props}>
      {badge()}
      <g transform="translate(2.71 2.85) scale(0.184)">
        <g fill="none" stroke={FIGURE} strokeWidth={7} strokeLinecap="round" strokeLinejoin="round">
          <line x1="21" y1="30" x2="21" y2="91" />
          <line x1="21" y1="79" x2="50" y2="79" />
          <line x1="50" y1="79" x2="50" y2="91" />
        </g>
        <g fill="none" stroke={FIGURE} strokeWidth={9} strokeLinecap="round" strokeLinejoin="round">
          <line x1="39" y1="29" x2="39" y2="61" />
          <polyline points="39,34 55,48 65,59" />
          <line x1="39" y1="61" x2="68" y2="61" />
          <line x1="68" y1="61" x2="68" y2="88" />
          <line x1="68" y1="88" x2="79" y2="88" />
        </g>
        <circle cx="39" cy="16" r="11" fill={FIGURE} />
      </g>
    </svg>
  );
}

/** OUTSTANDING — amber badge with no figure (open obligations that are neither on the member nor awaited by them). */
export function OutstandingBadge(props: SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" {...props}>
      {badge(OUTSTANDING_FILL, OUTSTANDING_BORDER)}
    </svg>
  );
}
